import PocketBase from "pocketbase";
import {
  Category,
  Product,
  ProductForm,
  Admin,
  AdminForm,
  Shop,
  ShopForm,
  Order,
  OrderForm,
  User,
  UserForm,
  UserAddress,
  UserAddressForm,
  Invoice,
  InvoiceForm,
  Message,
  MessageForm,
  StatsSummary,
  ApiResponse,
  Comment,
  CommentForm,
} from "../types";
import { POCKETBASE_URL } from "./config";

export const pb = new PocketBase(POCKETBASE_URL);

const AUTH_ROLE_KEY = "auth_role";

export type AuthRole = "admin" | "shop";

export const setAuthRole = (role: AuthRole) => {
  sessionStorage.setItem(AUTH_ROLE_KEY, role);
};

export const getAuthRole = (): AuthRole => {
  return sessionStorage.getItem(AUTH_ROLE_KEY) === "shop" ? "shop" : "admin";
};

export const isShop = () => getAuthRole() === "shop";

// Auth helpers
export const isAuthenticated = () => pb.authStore.isValid;

export const getCurrentUser = () => pb.authStore.model;

export const logout = () => {
  pb.authStore.clear();
  sessionStorage.removeItem(AUTH_ROLE_KEY);
};

export const loginAsAdmin = async (email: string, password: string) => {
  await pb.collection("admins").authWithPassword(email, password);
  setAuthRole("admin");
};

export const loginAsShop = async (email: string, password: string) => {
  await pb.collection("shops").authWithPassword(email, password);
  const shop = pb.authStore.model as { is_active?: boolean } | null;
  if (shop?.is_active !== true) {
    logout();
    throw new Error("shop_not_verified");
  }
  setAuthRole("shop");
};

// Helper function to convert RecordModel to Category
const convertToCategory = (record: any): Category => ({
  id: record.id,
  title: record.title || "",
  title_en: record.title_en || "",
  avatar: record.avatar || "",
  parent_id: record.parent_id || "",
  created: record.created,
  updated: record.updated,
  expand: record.expand
    ? {
        parent_id: record.expand.parent_id
          ? convertToCategory(record.expand.parent_id)
          : undefined,
      }
    : undefined,
});

// Helper function to convert RecordModel to Product
const convertToProduct = (record: any): Product => ({
  id: record.id,
  title: record.title || "",
  title_en: record.title_en || "",
  caption: record.caption || "",
  weight: record.weight || 0,
  price: record.price || 0,
  make_price: record.make_price || 0,
  segment: record.segment || "products",
  avatar: record.avatar || "",
  photos: record.photos || [],
  type: record.type || "2d",
  created: record.created,
  updated: record.updated,
  expand: record.expand
    ? {
        category_id: record.expand.category_id
          ? convertToCategory(record.expand.category_id)
          : undefined,
      }
    : undefined,
});

// Helper function to convert RecordModel to Admin
const convertToAdmin = (record: any): Admin => ({
  id: record.id,
  email: record.email || "",
  verified: record.verified || false,
  emailVisibility: record.emailVisibility || false,
  access: record.access || [],
  is_super_admin: record.is_super_admin || false,
  created: record.created,
  updated: record.updated,
});

// Category API helpers
export const getCategories = async (
  page = 1,
  perPage = 10,
  parentId?: string
): Promise<ApiResponse<Category>> => {
  const filter = parentId ? `parent_id = "${parentId}"` : 'parent_id = ""';

  const response = await pb.collection("categories").getList(page, perPage, {
    filter,
    expand: "parent_id",
    sort: "-created",
    requestKey: `categories_${page}_${perPage}_${parentId || "root"}`, // Prevent auto-cancellation for different requests
  });

  return {
    items: response.items.map(convertToCategory),
    page: response.page,
    perPage: response.perPage,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
  };
};

export const getCategory = async (id: string): Promise<Category> => {
  const record = await pb.collection("categories").getOne(id, {
    expand: "parent_id",
  });
  return convertToCategory(record);
};

export const createCategory = async (data: FormData): Promise<Category> => {
  const record = await pb.collection("categories").create(data);
  return convertToCategory(record);
};

export const updateCategory = async (
  id: string,
  data: FormData
): Promise<Category> => {
  const record = await pb.collection("categories").update(id, data);
  return convertToCategory(record);
};

export const deleteCategory = async (id: string): Promise<void> => {
  await pb.collection("categories").delete(id);
};

export const getAllCategories = async (): Promise<Category[]> => {
  const records = await pb.collection("categories").getFullList({
    expand: "parent_id",
    sort: "-created",
    requestKey: "all_categories", // Prevent auto-cancellation for this specific request
  });

  return records.map(convertToCategory);
};

// Product API helpers
export const getProducts = async (
  page = 1,
  perPage = 10,
  categoryId?: string,
  searchTerm?: string
): Promise<ApiResponse<Product>> => {
  let filter = "";

  if (categoryId) {
    filter += `category_id = "${categoryId}"`;
  }

  if (searchTerm) {
    const searchFilter = `(title ~ "${searchTerm}" || title_en ~ "${searchTerm}")`;
    if (filter) {
      filter += ` && ${searchFilter}`;
    } else {
      filter = searchFilter;
    }
  }

  const requestOptions: any = {
    expand: "category_id",
    sort: "-created",
    requestKey: `products_${page}_${perPage}_${categoryId || "all"}_${
      searchTerm || "no-search"
    }`,
  };

  // Only add filter if it's not empty
  if (filter) {
    requestOptions.filter = filter;
  }

  const response = await pb
    .collection("products")
    .getList(page, perPage, requestOptions);

  return {
    items: response.items.map(convertToProduct),
    page: response.page,
    perPage: response.perPage,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
  };
};

export const getProduct = async (id: string): Promise<Product> => {
  const record = await pb.collection("products").getOne(id, {
    expand: "category_id",
  });
  return convertToProduct(record);
};

export const createProduct = async (data: FormData): Promise<Product> => {
  const record = await pb.collection("products").create(data);
  return convertToProduct(record);
};

export const updateProduct = async (
  id: string,
  data: FormData | { [key: string]: any }
): Promise<Product> => {
  const record = await pb.collection("products").update(id, data);
  return convertToProduct(record);
};

export const deleteProduct = async (id: string): Promise<void> => {
  await pb.collection("products").delete(id);
};

export const getAllProducts = async (): Promise<Product[]> => {
  const records = await pb.collection("products").getFullList({
    expand: "category_id",
    sort: "-created",
    requestKey: "all_products",
  });

  return records.map(convertToProduct);
};

// Admin API helpers
export const getAdmins = async (
  page = 1,
  perPage = 10
): Promise<ApiResponse<Admin>> => {
  const response = await pb.collection("admins").getList(page, perPage, {
    sort: "-created",
    requestKey: `admins_${page}_${perPage}`,
  });

  return {
    items: response.items.map(convertToAdmin),
    page: response.page,
    perPage: response.perPage,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
  };
};

export const getAdmin = async (id: string): Promise<Admin> => {
  const record = await pb.collection("admins").getOne(id);
  return convertToAdmin(record);
};

export const createAdmin = async (data: any): Promise<Admin> => {
  const record = await pb.collection("admins").create(data);
  return convertToAdmin(record);
};

export const updateAdmin = async (id: string, data: any): Promise<Admin> => {
  const record = await pb.collection("admins").update(id, data);
  return convertToAdmin(record);
};

export const deleteAdmin = async (id: string): Promise<void> => {
  await pb.collection("admins").delete(id);
};

const readShopIsActive = (record: { is_active?: boolean }): boolean =>
  record.is_active === true;

const convertToShop = (record: any): Shop => ({
  id: record.id,
  email: record.email || "",
  is_active: readShopIsActive(record),
  verified: record.verified ?? false,
  emailVisibility: record.emailVisibility ?? false,
  title: record.title || "",
  address: record.address || "",
  location: record.location,
  phone: record.phone || "",
  slug: record.slug || "",
  created: record.created,
  updated: record.updated,
});

export const getShops = async (
  page = 1,
  perPage = 10
): Promise<ApiResponse<Shop>> => {
  const response = await pb.collection("shops").getList(page, perPage, {
    sort: "-created",
    requestKey: `shops_${page}_${perPage}`,
  });

  return {
    items: response.items.map(convertToShop),
    page: response.page,
    perPage: response.perPage,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
  };
};

export const getShop = async (id: string): Promise<Shop> => {
  const record = await pb.collection("shops").getOne(id);
  return convertToShop(record);
};

export const createShop = async (data: ShopForm): Promise<Shop> => {
  const payload: Record<string, unknown> = {
    email: data.email,
    password: data.password,
    passwordConfirm: data.password,
    is_active: data.is_active,
    title: data.title || "",
    address: data.address || "",
    phone: data.phone || "",
    slug: data.slug || "",
  };
  const record = await pb.collection("shops").create(payload);
  return convertToShop(record);
};

export const updateShop = async (
  id: string,
  data: Partial<ShopForm>
): Promise<Shop> => {
  const payload: Record<string, unknown> = {};
  if (data.email !== undefined) payload.email = data.email;
  if (data.is_active !== undefined) payload.is_active = data.is_active;
  if (data.title !== undefined) payload.title = data.title;
  if (data.address !== undefined) payload.address = data.address;
  if (data.phone !== undefined) payload.phone = data.phone;
  if (data.slug !== undefined) payload.slug = data.slug;
  if (data.password) {
    payload.password = data.password;
    payload.passwordConfirm = data.password;
  }
  const record = await pb.collection("shops").update(id, payload);
  return convertToShop(record);
};

export const deleteShop = async (id: string): Promise<void> => {
  await pb.collection("shops").delete(id);
};

// Helper function to convert RecordModel to Order
const convertToOrder = (record: any): Order => ({
  id: record.id,
  user_id: record.user_id || "",
  user_address: record.user_address || "",
  title: record.title || "",
  description: record.description || "",
  suggestPrice: record.suggestPrice || 0,
  preDeposit: record.preDeposit || 0,
  makePrice: record.makePrice || 0,
  percentMake: record.percentMake || 0,
  postDeposit: record.postDeposit || 0,
  order_type: record.order_type || "2d",
  weight: record.weight || 0,
  confirmToMake: record.confirmToMake || false,
  cancel: record.cancel || false,
  cancel_by_who: record.cancel_by_who || "user",
  status: record.status || "preDeposit",
  goldPrice: record.goldPrice || 0,
  how_to_deliver: record.how_to_deliver || "postal",
  refId: record.refId || "",
  gatewayName: record.gatewayName || "zarinpal",
  last_message_date: record.last_message_date || "",
  created: record.created,
  updated: record.updated,
  expand: record.expand
    ? {
        user_id: record.expand.user_id,
        orders_option: record.expand.orders_option
          ? record.expand.orders_option.map((option: any) => ({
              id: option.id,
              title: option.title || "",
              price: option.price || 0,
              order_id: option.order_id || "",
              created: option.created,
              updated: option.updated,
            }))
          : [],
      }
    : undefined,
});

// Order API helpers
export const getOrders = async (
  page = 1,
  perPage = 10
): Promise<ApiResponse<Order>> => {
  const response = await pb.collection("orders").getList(page, perPage, {
    expand: "user_id,orders_option",
    sort: "-created",
    requestKey: `orders_${page}_${perPage}`,
  });

  return {
    items: response.items.map(convertToOrder),
    page: response.page,
    perPage: response.perPage,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
  };
};

export const getOrder = async (id: string): Promise<Order> => {
  const record = await pb.collection("orders").getOne(id, {
    expand: "user_id,orders_option",
  });
  return convertToOrder(record);
};

export const createOrder = async (data: any): Promise<Order> => {
  const record = await pb.collection("orders").create(data);
  return convertToOrder(record);
};

export const updateOrder = async (id: string, data: any): Promise<Order> => {
  const record = await pb.collection("orders").update(id, data);
  return convertToOrder(record);
};

export const deleteOrder = async (id: string): Promise<void> => {
  await pb.collection("orders").delete(id);
};

export const getAllOrders = async (): Promise<Order[]> => {
  const records = await pb.collection("orders").getFullList({
    expand: "user_id,orders_option",
    sort: "-created",
    requestKey: "all_orders",
  });

  return records.map(convertToOrder);
};

export const getTodayOrders = async (): Promise<Order[]> => {
  const today = new Date();
  const startOfDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const endOfDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59
  );

  const records = await pb.collection("orders").getFullList({
    filter: `created >= "${startOfDay.toISOString()}" && created <= "${endOfDay.toISOString()}"`,
    expand: "user_id,orders_option",
    sort: "-created",
    requestKey: "today_orders",
  });

  return records.map(convertToOrder);
};

// Helper function to convert RecordModel to User
const convertToUser = (record: any): User => ({
  id: record.id,
  email: record.email || "",
  emailVisibility: record.emailVisibility || false,
  verified: record.verified || false,
  first_name: record.first_name || "",
  last_name: record.last_name || "",
  avatar: record.avatar || "",
  phone: record.phone || "",
  nationalCode: record.nationalCode || "",
  sex: record.sex || undefined,
  is_online: record.is_online || false,
  created: record.created,
  updated: record.updated,
  expand: undefined,
});

// Helper function to convert RecordModel to UserAddress
const convertToUserAddress = (record: any): UserAddress => ({
  id: record.id,
  title: record.title || "",
  address: record.address || "",
  postalCode: record.postalCode || "",
  created: record.created,
  updated: record.updated,
});

// User API helpers
export const getUsers = async (
  page = 1,
  perPage = 10,
  searchTerm?: string
): Promise<ApiResponse<User>> => {
  const options: any = {
    sort: "-created",
    requestKey: `users_${page}_${perPage}_${searchTerm || ""}`,
  };

  if (searchTerm) {
    options.filter = `first_name ~ "${searchTerm}" || last_name ~ "${searchTerm}" || email ~ "${searchTerm}" || phone ~ "${searchTerm}" || nationalCode ~ "${searchTerm}"`;
  }

  const response = await pb.collection("users").getList(page, perPage, options);

  return {
    items: response.items.map(convertToUser),
    page: response.page,
    perPage: response.perPage,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
  };
};

export const getUser = async (id: string): Promise<User> => {
  const record = await pb.collection("users").getOne(id);
  return convertToUser(record);
};

export const createUser = async (data: any): Promise<User> => {
  const record = await pb.collection("users").create(data);
  return convertToUser(record);
};

export const updateUser = async (id: string, data: any): Promise<User> => {
  const record = await pb.collection("users").update(id, data);
  return convertToUser(record);
};

export const deleteUser = async (id: string): Promise<void> => {
  await pb.collection("users").delete(id);
};

export const getAllUsers = async (): Promise<User[]> => {
  const records = await pb.collection("users").getFullList({
    sort: "-created",
    requestKey: "all_users",
  });

  return records.map(convertToUser);
};

// User Address API helpers
export const getUserAddresses = async (
  userId: string
): Promise<UserAddress[]> => {
  const records = await pb.collection("users_address").getFullList({
    filter: `user_id = "${userId}"`,
    sort: "-created",
    requestKey: `user_addresses_${userId}`,
  });

  return records.map(convertToUserAddress);
};

export const createUserAddress = async (data: any): Promise<UserAddress> => {
  const record = await pb.collection("users_address").create(data);
  return convertToUserAddress(record);
};

export const updateUserAddress = async (
  id: string,
  data: any
): Promise<UserAddress> => {
  const record = await pb.collection("users_address").update(id, data);
  return convertToUserAddress(record);
};

export const deleteUserAddress = async (id: string): Promise<void> => {
  await pb.collection("users_address").delete(id);
};

// Helper function to convert RecordModel to Invoice
const convertToInvoice = (record: any): Invoice => ({
  id: record.id,
  user_id: record.user_id || "",
  order_id: record.order_id || "",
  step: record.step || "preDeposit",
  expireDate: record.expireDate || "",
  amount: record.amount || 0,
  authority: record.authority || "",
  refId: record.refId || "",
  created: record.created,
  updated: record.updated,
  expand: record.expand
    ? {
        user_id: record.expand.user_id
          ? convertToUser(record.expand.user_id)
          : undefined,
        order_id: record.expand.order_id
          ? convertToOrder(record.expand.order_id)
          : undefined,
      }
    : undefined,
});

// Helper function to convert RecordModel to Message
const convertToMessage = (record: any): Message => ({
  id: record.id,
  user_id: record.user_id || "",
  admin_id: record.admin_id || "",
  message_type: record.message_type || "text",
  confirm: record.confirm || "none",
  text_detail: record.text_detail || [],
  text: record.text || "",
  file: record.file || "",
  product_id: record.product_id || "",
  is_seen: record.is_seen || false,
  created: record.created,
  updated: record.updated,
  expand: record.expand
    ? {
        user_id: record.expand.user_id
          ? convertToUser(record.expand.user_id)
          : undefined,
        admin_id: record.expand.admin_id
          ? convertToAdmin(record.expand.admin_id)
          : undefined,
        product_id: record.expand.product_id
          ? convertToProduct(record.expand.product_id)
          : undefined,
      }
    : undefined,
});

// Invoice API helpers
export const getInvoices = async (
  page = 1,
  perPage = 10,
  userId?: string
): Promise<ApiResponse<Invoice>> => {
  let filter = "";
  if (userId) {
    filter = `user_id = "${userId}"`;
  }

  const requestOptions: any = {
    expand: "user_id,order_id",
    sort: "-created",
    requestKey: `invoices_${page}_${perPage}_${userId || "all"}`,
  };

  if (filter) {
    requestOptions.filter = filter;
  }

  const response = await pb
    .collection("invoices")
    .getList(page, perPage, requestOptions);

  return {
    items: response.items.map(convertToInvoice),
    page: response.page,
    perPage: response.perPage,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
  };
};

export const getUserInvoices = async (userId: string): Promise<Invoice[]> => {
  const records = await pb.collection("invoices").getFullList({
    // filter: `user_id = "${userId}"`,
    // expand: "user_id,order_id",
    // sort: "-created",
    // requestKey: `user_invoices_${userId}`,
  });

  //return records.map(convertToInvoice);
  return [];
};

export const createInvoice = async (data: any): Promise<Invoice> => {
  const record = await pb.collection("invoices").create(data);
  return convertToInvoice(record);
};

export const updateInvoice = async (
  id: string,
  data: any
): Promise<Invoice> => {
  const record = await pb.collection("invoices").update(id, data);
  return convertToInvoice(record);
};

export const deleteInvoice = async (id: string): Promise<void> => {
  await pb.collection("invoices").delete(id);
};

// Message API helpers
export const getMessages = async (
  page = 1,
  perPage = 50,
  userId?: string
): Promise<ApiResponse<Message>> => {
  let filter = "";
  if (userId) {
    filter = `user_id = "${userId}"`;
  }

  const requestOptions: any = {
    expand: "user_id,admin_id,product_id",
    sort: "-created",
    requestKey: `messages_${page}_${perPage}_${userId || "all"}`,
  };

  if (filter) {
    requestOptions.filter = filter;
  }

  const response = await pb
    .collection("messages")
    .getList(page, perPage, requestOptions);

  return {
    items: response.items.map(convertToMessage),
    page: response.page,
    perPage: response.perPage,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
  };
};

export const getUserMessages = async (userId: string): Promise<Message[]> => {
  const records = await pb.collection("messages").getFullList({
    filter: `user_id = "${userId}"`,
    expand: "user_id,admin_id,product_id",
    sort: "created",
    requestKey: `user_messages_${userId}`,
  });

  return records.map(convertToMessage);
};

export const createMessage = async (data: FormData): Promise<Message> => {
  const record = await pb.collection("messages").create(data);
  return convertToMessage(record);
};

export const updateMessage = async (
  id: string,
  data: any
): Promise<Message> => {
  const record = await pb.collection("messages").update(id, data);
  return convertToMessage(record);
};

export const deleteMessage = async (id: string): Promise<void> => {
  await pb.collection("messages").delete(id);
};

export const markMessageAsSeen = async (id: string): Promise<Message> => {
  const record = await pb.collection("messages").update(id, { is_seen: true });
  return convertToMessage(record);
};

// Get users with recent messages for chat sidebar
export const getUsersWithMessages = async (): Promise<User[]> => {
  // First, get all messages to find unique user IDs
  const messages = await pb.collection("messages").getFullList({
    fields: "user_id",
    requestKey: "messages_user_ids",
  });

  // Extract unique user IDs from messages
  const uniqueUserIds = Array.from(new Set(messages.map((msg) => msg.user_id)));

  if (uniqueUserIds.length === 0) {
    return [];
  }

  // Get users who have messages
  const records = await pb.collection("users").getFullList({
    filter: uniqueUserIds.map((id) => `id = "${id}"`).join(" || "),
    sort: "-updated",
    requestKey: "users_with_messages",
  });

  return records.map(convertToUser);
};

// Access control helpers
export const hasAccess = (
  access: string[] | undefined,
  requiredAccess: string
): boolean => {
  if (!access) return false;
  return (
    access.includes(requiredAccess as any) || access.includes("super_admin")
  );
};

export const isSuperAdmin = (): boolean => {
  if (isShop()) return false;
  const user = getCurrentUser();
  return user?.is_super_admin || false;
};

export const canAccessProducts = (): boolean => {
  if (isShop()) return false;
  const user = getCurrentUser();
  return hasAccess(user?.access, "products") || isSuperAdmin();
};

export const canAccessCategories = (): boolean => {
  if (isShop()) return false;
  const user = getCurrentUser();
  return hasAccess(user?.access, "category") || isSuperAdmin();
};

export const canAccessOrders = (): boolean => {
  if (isShop()) return false;
  const user = getCurrentUser();
  return hasAccess(user?.access, "orders") || isSuperAdmin();
};

export const canAccessUsers = (): boolean => {
  if (isShop()) return false;
  const user = getCurrentUser();
  return hasAccess(user?.access, "users") || isSuperAdmin();
};

export const canAccessChat = (): boolean => {
  if (isShop()) return true;
  const user = getCurrentUser();
  return hasAccess(user?.access, "chat") || isSuperAdmin();
};

export const canAccessComments = (): boolean => {
  if (isShop()) return false;
  return true;
};

export const canManageAdmins = (): boolean => {
  if (isShop()) return false;
  return isSuperAdmin();
};

export const canManageShops = (): boolean => {
  if (isShop()) return false;
  return isSuperAdmin();
};

export const canAccessDashboard = (): boolean => {
  return true;
};

// Helper function to convert RecordModel to Comment
const convertToComment = (record: any): Comment => ({
  id: record.id,
  text: record.text || "",
  rate: record.rate || 0,
  user_id: record.user_id || "",
  is_active: record.is_active ?? true,
  created: record.created,
  updated: record.updated,
  expand: record.expand
    ? {
        user_id: record.expand.user_id
          ? convertToUser(record.expand.user_id)
          : undefined,
      }
    : undefined,
});

// Comments API helpers
export const getComments = async (
  page = 1,
  perPage = 50
): Promise<ApiResponse<Comment>> => {
  const response = await pb.collection("comments").getList(page, perPage, {
    expand: "user_id",
    sort: "-created",
    requestKey: `comments_${page}_${perPage}`,
  });

  return {
    items: response.items.map(convertToComment),
    page: response.page,
    perPage: response.perPage,
    totalItems: response.totalItems,
    totalPages: response.totalPages,
  };
};

export const getAllComments = async (): Promise<Comment[]> => {
  const records = await pb.collection("comments").getFullList({
    expand: "user_id",
    sort: "-created",
    requestKey: "all_comments",
  });

  return records.map(convertToComment);
};

export const getComment = async (id: string): Promise<Comment> => {
  const record = await pb.collection("comments").getOne(id, {
    expand: "user_id",
  });
  return convertToComment(record);
};

export const createComment = async (data: CommentForm): Promise<Comment> => {
  const record = await pb.collection("comments").create(data);
  return convertToComment(record);
};

export const updateComment = async (
  id: string,
  data: Partial<CommentForm>
): Promise<Comment> => {
  const record = await pb.collection("comments").update(id, data);
  return convertToComment(record);
};

export const deleteComment = async (id: string): Promise<void> => {
  await pb.collection("comments").delete(id);
};

// Stats API
export const getStatsSummary = async (): Promise<StatsSummary> => {
  const response = await pb.collection("stats_summary").getList(1, 1);
  const record = response.items[0];

  return {
    id: record.id,
    collectionId: record.collectionId,
    collectionName: record.collectionName,
    current_month_orders: record.current_month_orders || 0,
    total_orders: record.total_orders || 0,
    total_products: record.total_products || 0,
    total_users: record.total_users || 0,
    current_month_pending_orders: record.current_month_pending_orders || 0,
    current_month_done_orders: record.current_month_done_orders || 0,
    current_month_delivery_orders: record.current_month_delivery_orders || 0,
  };
};
